import React, { useRef, useState, useEffect } from 'react';
import { asBlob } from 'html-docx-js-typescript';
import ProTemplate from '../templates/ProTemplate';
import ModernTemplate from '../templates/ModernTemplate';
import MinimalTemplate from '../templates/MinimalTemplate';
import AtsScore from './ATSScore';
import ContentSuggestions from './ContentSuggestions';
import './ResumePreview.css';

const templates = { pro: ProTemplate, modern: ModernTemplate, minimal: MinimalTemplate };

const ResumePreview = ({ data, template, layout, sectionVisibility, sectionOrder, addToast }) => {
  const previewRef = useRef(null);
  const contentMeasureRef = useRef(null);
  const pageHeightRef = useRef(null);
  const [showAts, setShowAts] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const [pageHeightPx, setPageHeightPx] = useState(0);

  useEffect(() => {
    if (contentMeasureRef.current && pageHeightRef.current) {
      const total = contentMeasureRef.current.scrollHeight;
      const ph = pageHeightRef.current.clientHeight;
      setPageHeightPx(ph);
      setPageCount(Math.max(1, Math.ceil(total / ph)));
    }
  }, [data, template, layout, sectionVisibility]);

  const TemplateComponent = templates[template] || templates.pro;

  const previewStyle = {
    fontFamily: data.theme?.font || 'Arial',
    fontSize: data.theme?.fontSize || '14px'
  };

  const pageStyle = {
    fontFamily: data.theme?.font || 'Arial',
    fontSize: data.theme?.fontSize || '14px',
    lineHeight: data.theme?.lineHeight || '1.6',
    color: data.theme?.primaryColor || '#2c3e50'
  };

  const renderTemplate = () => (
    <TemplateComponent
      data={data}
      layout={layout}
      sectionVisibility={sectionVisibility}
      sectionOrder={sectionOrder}
      theme={data.theme}
    />
  );

  const handleDownloadPDF = async () => {
    setIsExporting(true);
    try {
      const { jsPDF } = await import('jspdf');
      const html2canvas = (await import('html2canvas')).default;

      const pages = previewRef.current.querySelectorAll('.preview-page:not(.preview-page-measure)');
      if (!pages.length) { addToast('Preview not found', 'error'); return; }

      const pdf = new jsPDF('p', 'mm', 'a4');

      for (let i = 0; i < pages.length; i++) {
        const pageEl = pages[i];

        const canvas = await html2canvas(pageEl, {
          scale: 5,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          imageTimeout: 0,
        });

        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const imgWidth = 210;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, Math.min(imgHeight, 297));
      }

      pdf.save(`${data.personalInfo.fullName || 'Resume'}.pdf`);
      addToast('PDF downloaded successfully!', 'success');
    } catch (err) {
      console.error('PDF export error:', err);
      addToast('PDF export failed. Try DOCX instead.', 'error');
    }
    setIsExporting(false);
  };

  const handleDownloadDOCX = async () => {
    setIsExporting(true);
    try {
      const pages = previewRef.current.querySelectorAll('.preview-page:not(.preview-page-measure)');
      if (!pages.length) { addToast('Preview not found', 'error'); return; }

      let bodyHtml = '';
      pages.forEach((page, i) => {
        const content = page.querySelector('div:first-child').innerHTML;
        if (i > 0) bodyHtml += '<div style="page-break-before: always; margin-top: 40px;"></div>';
        bodyHtml += content;
      });

      const styles = `
        <style>
          @page { size: A4; margin: 1in; }
          body { font-family: ${previewStyle.fontFamily}; font-size: ${previewStyle.fontSize}; line-height: 1.6; color: #333; }
          h2 { color: #2c3e50; font-size: 24px; }
          h3 { color: #34495e; font-size: 18px; border-bottom: 2px solid #3498db; padding-bottom: 8px; }
          a { color: #3498db; text-decoration: none; }
          .skill-tag { display: inline-block; background: #e1f5fe; color: #0288d1; padding: 4px 10px; border-radius: 12px; font-size: 12px; margin: 2px; }
        </style>
      `;

      const htmlContent = `<!DOCTYPE html><html><head><meta charset="UTF-8">${styles}</head><body>${bodyHtml}</body></html>`;

      const docxBlob = await asBlob(htmlContent, {
        orientation: 'portrait',
        margins: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
        title: data.personalInfo.fullName || 'Resume',
        fontSize: parseInt(previewStyle.fontSize),
        font: previewStyle.fontFamily,
      });

      const url = window.URL.createObjectURL(docxBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${data.personalInfo.fullName || 'Resume'}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      addToast('DOCX downloaded successfully!', 'success');
    } catch (err) {
      console.error('DOCX export error:', err);
      addToast('DOCX export failed.', 'error');
    }
    setIsExporting(false);
  };

  return (
    <div className="resume-preview-container">
      <div className="preview-toolbar">
        <div className="preview-toolbar-left">
          <span className="template-badge">{template.charAt(0).toUpperCase() + template.slice(1)}</span>
          <span className="layout-badge">{layout === 'single-column' ? 'Single' : 'Two'} Column</span>
          {pageCount > 1 && <span className="page-badge">{pageCount} pages</span>}
        </div>
        <div className="preview-toolbar-right">
          <button className="preview-action-btn" onClick={() => setShowAts(!showAts)} title="ATS Score">
            <span className="action-icon">📊</span> ATS
          </button>
          <button className="preview-action-btn" onClick={() => setShowSuggestions(!showSuggestions)} title="Content Suggestions">
            <span className="action-icon">💡</span> Tips
          </button>
          <div className="export-dropdown">
            <button className="export-btn-primary" disabled={isExporting} onClick={handleDownloadPDF}>
              {isExporting ? 'Exporting...' : 'Download PDF'}
            </button>
            <button className="export-btn-secondary" disabled={isExporting} onClick={handleDownloadDOCX} title="Download as DOCX">
              DOCX
            </button>
          </div>
        </div>
      </div>

      <div className="preview-body">
        <div className="preview-side-panels">
          {showAts && <AtsScore data={data} template={template} layout={layout} />}
          {showSuggestions && <ContentSuggestions data={data} onApply={(section, value) => {
            addToast('Suggestion applied!', 'success');
          }} />}
        </div>

        <div className="preview-page-wrapper" ref={previewRef} style={pageStyle}>
          {/* Hidden measuring container */}
          <div
            ref={pageHeightRef}
            className="preview-page preview-page-measure"
            aria-hidden="true"
          >
            <div ref={contentMeasureRef}>
              {renderTemplate()}
            </div>
          </div>

          {/* Visible pages */}
          <div className="preview-pages">
            {Array.from({ length: pageCount }, (_, i) => (
              <div key={i} className="preview-page">
                <div
                  style={i > 0 && pageHeightPx > 0
                    ? { marginTop: `-${i * pageHeightPx}px` }
                    : undefined
                  }
                >
                  {renderTemplate()}
                </div>
                {pageCount > 1 && (
                  <div className="page-number">{i + 1}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumePreview;
